// IELTS Exam Types and Configuration

export type IELTSExamType = 'speaking' | 'writing' | 'listening' | 'reading';

export type IELTSExamDuration = 'short' | 'medium' | 'full';

export interface IELTSDurationConfig {
  duration: IELTSExamDuration;
  label: string;
  description: string;
  speakingParts: number; // 1, 2, or 3 parts
  writingTasks: number; // 1 or 2 tasks
  readingPassages: number; // 1, 2, or 3 passages
  listeningSections: number; // 1, 2, 3, or 4 sections
  timeMinutes: number;
}

export interface IELTSExamConfig {
  type: IELTSExamType;
  label: string;
  description: string;
  icon: string;
  durations: IELTSDurationConfig[];
}

// Speaking Part Types
export interface IELTSSpeakingPart {
  part: 1 | 2 | 3;
  title: string;
  description: string;
  timeMinutes: number;
  questions: string[];
  preparationTime?: number; // Part 2 has 1 minute preparation
}

// Writing Task Types
export interface IELTSWritingTask {
  task: 1 | 2;
  title: string;
  description: string;
  prompt: string;
  wordCount: { min: number; max: number };
  timeMinutes: number;
  type: 'academic' | 'general';
}

// Reading Question Types
export type IELTSReadingQuestionType =
  | 'multiple_choice'
  | 'true_false_not_given'
  | 'matching_headings'
  | 'fill_in_blanks'
  | 'short_answer';

export interface IELTSReadingQuestion {
  id: string;
  type: IELTSReadingQuestionType;
  question: string;
  options?: string[]; // For multiple choice
  correctAnswer: string | string[]; // Can be single or multiple answers
  explanation?: string;
}

export interface IELTSReadingPassage {
  id: string;
  title: string;
  text: string;
  questions: IELTSReadingQuestion[];
  timeMinutes: number;
}

// Listening Question Types
export type IELTSListeningQuestionType =
  | 'multiple_choice'
  | 'fill_in_blanks'
  | 'matching'
  | 'map_labeling'
  | 'short_answer';

export interface IELTSListeningQuestion {
  id: string;
  type: IELTSListeningQuestionType;
  question: string;
  options?: string[]; // For multiple choice
  correctAnswer: string | string[];
  explanation?: string;
}

export interface IELTSListeningSection {
  id: string;
  sectionNumber: 1 | 2 | 3 | 4;
  title: string;
  description: string;
  audioUrl?: string;
  transcript?: string;
  questions: IELTSListeningQuestion[];
  timeMinutes: number;
}

// Evaluation Types
export interface IELTSSpeakingEvaluation {
  overallBand: number;
  fluencyAndCoherence: number;
  lexicalResource: number;
  grammaticalRange: number;
  pronunciation: number;
  feedback: string;
  strengths: string[];
  improvements: string[];
}

export interface IELTSWritingEvaluation {
  overallBand: number;
  taskAchievement: number;
  coherenceAndCohesion: number;
  lexicalResource: number;
  grammaticalRange: number;
  feedback: string;
  strengths: string[];
  improvements: string[];
}

export interface IELTSReadingEvaluation {
  overallBand: number;
  correctAnswers: number;
  totalQuestions: number;
  percentageCorrect: number;
  feedback: string;
  strengths: string[];
  improvements: string[];
}

export interface IELTSListeningEvaluation {
  overallBand: number;
  correctAnswers: number;
  totalQuestions: number;
  percentageCorrect: number;
  feedback: string;
  strengths: string[];
  improvements: string[];
}

// Session Types
export interface IELTSSpeakingSession {
  examType: 'speaking';
  duration: IELTSExamDuration;
  currentPart: number;
  parts: IELTSSpeakingPart[];
  responses: {
    part: number;
    questionIndex: number;
    audioUri?: string;
    transcription?: string;
  }[];
  evaluation?: IELTSSpeakingEvaluation;
  status: 'setup' | 'in-progress' | 'completed';
}

export interface IELTSWritingSession {
  examType: 'writing';
  duration: IELTSExamDuration;
  currentTask: number;
  tasks: IELTSWritingTask[];
  responses: {
    task: number;
    text: string;
    wordCount: number;
  }[];
  evaluation?: IELTSWritingEvaluation;
  status: 'setup' | 'in-progress' | 'completed';
}

export interface IELTSReadingSession {
  examType: 'reading';
  duration: IELTSExamDuration;
  currentPassage: number;
  passages: IELTSReadingPassage[];
  answers: {
    questionId: string;
    answer: string | string[];
  }[];
  evaluation?: IELTSReadingEvaluation;
  status: 'setup' | 'in-progress' | 'completed';
}

export interface IELTSListeningSession {
  examType: 'listening';
  duration: IELTSExamDuration;
  currentSection: number;
  sections: IELTSListeningSection[];
  answers: {
    questionId: string;
    answer: string | string[];
  }[];
  evaluation?: IELTSListeningEvaluation;
  status: 'setup' | 'loading' | 'in-progress' | 'completed';
}

export type IELTSSession = IELTSSpeakingSession | IELTSWritingSession | IELTSReadingSession | IELTSListeningSession;

// Exam Configurations
export const IELTS_EXAM_CONFIGS: IELTSExamConfig[] = [
  {
    type: 'speaking',
    label: 'Speaking',
    description: 'Practice your spoken English with realistic interview questions',
    icon: 'microphone',
    durations: [
      {
        duration: 'short',
        label: 'Quick Practice',
        description: 'Part 1 only - Introduction & Interview',
        speakingParts: 1,
        writingTasks: 0,
        readingPassages: 0,
        listeningSections: 0,
        timeMinutes: 5,
      },
      {
        duration: 'medium',
        label: 'Standard Practice',
        description: 'Parts 1 & 2 - Including topic card',
        speakingParts: 2,
        writingTasks: 0,
        readingPassages: 0,
        listeningSections: 0,
        timeMinutes: 10,
      },
      {
        duration: 'full',
        label: 'Full Exam',
        description: 'All 3 parts - Complete speaking test',
        speakingParts: 3,
        writingTasks: 0,
        readingPassages: 0,
        listeningSections: 0,
        timeMinutes: 14,
      },
    ],
  },
  {
    type: 'listening',
    label: 'Listening',
    description: 'Improve your listening comprehension skills',
    icon: 'headphones',
    durations: [
      {
        duration: 'short',
        label: 'Quick Practice',
        description: '1 section - 10 questions',
        speakingParts: 0,
        writingTasks: 0,
        readingPassages: 0,
        listeningSections: 1,
        timeMinutes: 10,
      },
      {
        duration: 'medium',
        label: 'Standard Practice',
        description: '2 sections - 20 questions',
        speakingParts: 0,
        writingTasks: 0,
        readingPassages: 0,
        listeningSections: 2,
        timeMinutes: 20,
      },
      {
        duration: 'full',
        label: 'Full Exam',
        description: '4 sections - 40 questions',
        speakingParts: 0,
        writingTasks: 0,
        readingPassages: 0,
        listeningSections: 4,
        timeMinutes: 40,
      },
    ],
  },
  {
    type: 'writing',
    label: 'Writing',
    description: 'Improve your academic and general writing skills',
    icon: 'pencil',
    durations: [
      {
        duration: 'short',
        label: 'Quick Practice',
        description: 'Task 1 only - 150 words',
        speakingParts: 0,
        writingTasks: 1,
        readingPassages: 0,
        listeningSections: 0,
        timeMinutes: 30,
      },
      {
        duration: 'medium',
        label: 'Standard Practice',
        description: 'Task 2 only - 250 words essay',
        speakingParts: 0,
        writingTasks: 1,
        readingPassages: 0,
        listeningSections: 0,
        timeMinutes: 45,
      },
      {
        duration: 'full',
        label: 'Full Exam',
        description: 'Both tasks - Complete writing test',
        speakingParts: 0,
        writingTasks: 2,
        readingPassages: 0,
        listeningSections: 0,
        timeMinutes: 60,
      },
    ],
  },
  {
    type: 'reading',
    label: 'Reading',
    description: 'Practice reading comprehension with academic passages',
    icon: 'book',
    durations: [
      {
        duration: 'short',
        label: 'Quick Practice',
        description: '1 passage with 13-14 questions',
        speakingParts: 0,
        writingTasks: 0,
        readingPassages: 1,
        listeningSections: 0,
        timeMinutes: 20,
      },
      {
        duration: 'medium',
        label: 'Standard Practice',
        description: '2 passages with 26-27 questions',
        speakingParts: 0,
        writingTasks: 0,
        readingPassages: 2,
        listeningSections: 0,
        timeMinutes: 40,
      },
      {
        duration: 'full',
        label: 'Full Exam',
        description: '3 passages with 40 questions',
        speakingParts: 0,
        writingTasks: 0,
        readingPassages: 3,
        listeningSections: 0,
        timeMinutes: 60,
      },
    ],
  },
];

// Speaking Topics with structured questions
export interface SpeakingTopic {
  topic: string;
  introduction: string;
  questions: string[];
}

export const SPEAKING_TOPICS_PART1: SpeakingTopic[] = [
  // SET 1
  {
    topic: 'Work & Career',
    introduction: "Let's talk about your work or studies.",
    questions: [
      "Do you work or are you a student?",
      "Why did you choose your job or field of study?",
      "What do you enjoy most about it?",
      "Do you think you will change your career path in the future?",
    ],
  },
  // SET 2
  {
    topic: 'Hometown',
    introduction: "Let's talk about where you live.",
    questions: [
      "Where are you from?",
      "What do you like most about your hometown?",
      "Do you live in a house or an apartment?",
      "Would you like to move somewhere else in the future?",
    ],
  },
  // SET 3
  {
    topic: 'Daily Routine',
    introduction: "Let's talk about your daily routine.",
    questions: [
      "What is your typical daily routine?",
      "When do you feel most productive during the day?",
      "Has your routine changed recently?",
      "Do you prefer mornings or evenings?",
    ],
  },
  // SET 4
  {
    topic: 'Free Time',
    introduction: "Let's talk about your free time.",
    questions: [
      "What do you do in your free time?",
      "Do you prefer indoor or outdoor activities?",
      "Did you have the same hobbies as a child?",
      "Would you like to try a new hobby?",
    ],
  },
  // SET 5
  {
    topic: 'Studies',
    introduction: "Let's talk about your studies.",
    questions: [
      "Do you work or are you a student?",
      "Is it difficult for you to study?",
      "What helps you most when studying?",
      "Do you prefer studying alone or with others?",
    ],
  },
  // SET 6
  {
    topic: 'Family',
    introduction: "Let's talk about your family.",
    questions: [
      "Tell me about your family.",
      "How often do you spend time together?",
      "Is family important in your life?",
      "Who do you admire most in your family?",
    ],
  },
  // SET 7
  {
    topic: 'Reading',
    introduction: "Let's talk about reading.",
    questions: [
      "Do you enjoy reading?",
      "What kind of books do you like?",
      "Did you read a lot as a child?",
      "Do you prefer printed or digital books?",
    ],
  },
  // SET 8
  {
    topic: 'Sports & Exercise',
    introduction: "Let's talk about sports and exercise.",
    questions: [
      "Do you play sports or exercise regularly?",
      "What sport is popular in your country?",
      "Did you enjoy sports at school?",
      "Do you prefer watching sports or playing them?",
    ],
  },
  // SPEAKING SET 1
  {
    topic: 'Work & Life Balance',
    introduction: "Let's talk about work and life.",
    questions: [
      "Do you work or are you a student?",
      "Why did you choose this job or subject?",
      "What do you enjoy most about it?",
      "Do you prefer mornings or evenings?",
    ],
  },
  // SPEAKING SET 2
  {
    topic: 'Your City',
    introduction: "Let's talk about your city.",
    questions: [
      "Tell me about your city.",
      "How long have you been living there?",
      "Do you think it's a good place to live?",
      "Would you like to move somewhere else?",
    ],
  },
  // SPEAKING SET 3
  {
    topic: 'Morning Routine',
    introduction: "Let's talk about your mornings.",
    questions: [
      "What do you normally do in the morning?",
      "Is exercise part of your routine?",
      "Do you have enough free time?",
      "What do you like doing on weekends?",
    ],
  },
  // SPEAKING SET 4
  {
    topic: 'Home',
    introduction: "Let's talk about your home.",
    questions: [
      "Do you live in a house or an apartment?",
      "What is your favourite room?",
      "What facilities are in your neighbourhood?",
      "What would you change about your area?",
    ],
  },
  // SPEAKING SET 5
  {
    topic: 'Hobbies',
    introduction: "Let's talk about your hobbies.",
    questions: [
      "What do you do in your free time?",
      "Do you prefer indoor or outdoor activities?",
      "Did you have the same hobbies as a child?",
      "Would you like to try a new hobby?",
    ],
  },
  // SPEAKING SET 6
  {
    topic: 'Education',
    introduction: "Let's talk about your education.",
    questions: [
      "What do you study?",
      "What is your favourite subject?",
      "Do you prefer studying alone or with others?",
      "Do you think English will be important in your future?",
    ],
  },
  // SPEAKING SET 7
  {
    topic: 'Career',
    introduction: "Let's talk about your career.",
    questions: [
      "Do you work or are you a student?",
      "What is the most important part of your job or studies?",
      "Do you work/study alone or with others?",
      "Do you think work-life balance is important?",
    ],
  },
  // SPEAKING SET 8
  {
    topic: 'Country',
    introduction: "Let's talk about your country.",
    questions: [
      "Where are you from?",
      "What do you like about your country?",
      "Is your country popular with tourists?",
      "Tell me about the people in your country.",
    ],
  },
  // SPEAKING SET 9
  {
    topic: 'Evening Routine',
    introduction: "Let's talk about your evenings.",
    questions: [
      "What do you normally do after work or school?",
      "How much time do you spend relaxing?",
      "Do you prefer evenings or mornings?",
      "Has your routine changed recently?",
    ],
  },
  // SPEAKING SET 10
  {
    topic: 'School Memories',
    introduction: "Let's talk about your school days.",
    questions: [
      "What did you study at school?",
      "Who was your favourite teacher?",
      "Were you a good student?",
      "Did you have strict rules at school?",
    ],
  },
  // SPEAKING SET 11
  {
    topic: 'Work Hours',
    introduction: "Let's talk about your work.",
    questions: [
      "Do you work long hours?",
      "What is your dream job?",
      "What do you not like about your work or studies?",
      "Is there something useful you have learned recently?",
    ],
  },
  // SPEAKING SET 12
  {
    topic: 'Creative Hobbies',
    introduction: "Let's talk about creative activities.",
    questions: [
      "Do you have any creative hobbies?",
      "Do you enjoy reading or writing stories?",
      "Do you play a musical instrument?",
      "Do you prefer doing hobbies alone or with others?",
    ],
  },
];

export const SPEAKING_TOPICS_PART2: SpeakingTopic[] = [
  // SET 1
  {
    topic: 'Technology in Daily Life',
    introduction: "Let's speak about technology in daily life.",
    questions: [
      "How often do you use technology every day?",
      "What technological devices are most important for you?",
      "Has technology improved your life?",
      "Do you think people rely too much on technology?",
      "Could you live without your phone for a week?",
    ],
  },
  // SET 2
  {
    topic: 'Studying Online',
    introduction: "Let's speak about studying online.",
    questions: [
      "Have you ever taken an online course?",
      "What are the advantages of online learning?",
      "What challenges do students face when studying online?",
      "Do you prefer online or classroom learning?",
      "Will online education become more common?",
    ],
  },
  // SET 3
  {
    topic: 'Street Markets',
    introduction: "Let's speak about street markets.",
    questions: [
      "What can people usually find in street markets in your country?",
      "Do you personally enjoy visiting street markets? Why or why not?",
      "How are street markets different from shopping centres?",
      "Why do some people prefer traditional markets?",
      "Do you think street markets will disappear in the future?",
    ],
  },
  // SET 4
  {
    topic: 'Flowers',
    introduction: "Let's speak about flowers.",
    questions: [
      "Do you like flowers? Why or why not?",
      "Are flowers important in your country?",
      "On what occasions do people give flowers?",
      "Do flowers have special meanings in your culture?",
      "Do you think young people appreciate flowers as much as older people?",
    ],
  },
  // SET 5
  {
    topic: 'Movies',
    introduction: "Let's speak about movies.",
    questions: [
      "Do you enjoy watching movies?",
      "What kind of films do you prefer?",
      "Do you prefer watching films at home or at the cinema?",
      "Have your movie preferences changed over time?",
      "Are films popular in your country?",
    ],
  },
  // SET 6
  {
    topic: 'Birthdays',
    introduction: "Let's speak about birthdays.",
    questions: [
      "Do you enjoy celebrating your birthday?",
      "How are birthdays usually celebrated in your country?",
      "What is the most memorable birthday you have had?",
      "Do you prefer giving or receiving gifts?",
      "Have birthday celebrations changed over time?",
    ],
  },
  // SET 7
  {
    topic: 'News',
    introduction: "Let's speak about news.",
    questions: [
      "How often do you follow the news?",
      "Where do you usually get your news from?",
      "Is it important to stay informed?",
      "What kind of news interests you most?",
      "Do you discuss news with friends or family?",
    ],
  },
  // SET 8
  {
    topic: 'Food and Healthy Living',
    introduction: "Let's speak about food and healthy living.",
    questions: [
      "What is your favourite food?",
      "Do you usually eat healthy food?",
      "Do you enjoy cooking?",
      "Is fast food popular in your country?",
      "Do you think you have a healthy lifestyle?",
    ],
  },
  // SPEAKING SET 1
  {
    topic: 'Street Markets',
    introduction: "Let's speak about street markets.",
    questions: [
      "What can people usually see in street markets in your country?",
      "Do you often visit street markets? Why or why not?",
      "How are street markets different from shopping centres?",
      "Do you think street markets are becoming more or less popular?",
      "What role do street markets play in local communities?",
    ],
  },
  // SPEAKING SET 2
  {
    topic: 'Flowers',
    introduction: "Let's speak about flowers.",
    questions: [
      "Do you like flowers? Why or why not?",
      "What is your favourite flower?",
      "When do people usually give flowers in your country?",
      "Are flowers important in your culture?",
      "Do flowers have symbolic meanings?",
    ],
  },
  // SPEAKING SET 3
  {
    topic: 'Birthdays',
    introduction: "Let's speak about birthdays.",
    questions: [
      "Do you enjoy celebrating your birthday?",
      "How are birthdays celebrated in your country?",
      "What is the most memorable birthday you've had?",
      "Did you have birthday parties as a child?",
      "Do you prefer giving or receiving gifts?",
    ],
  },
  // SPEAKING SET 4
  {
    topic: 'Family',
    introduction: "Let's speak about family.",
    questions: [
      "Do you have a large or small family?",
      "How often do you spend time with your family?",
      "Who do you admire in your family?",
      "Is family important in your country?",
      "Have you celebrated something special recently?",
    ],
  },
  // SPEAKING SET 5
  {
    topic: 'Culture',
    introduction: "Let's speak about culture.",
    questions: [
      "How would you define culture?",
      "What traditional festivals are important in your country?",
      "What typical foods represent your culture?",
      "Is it important to learn about other cultures?",
      "Have you experienced culture shock?",
    ],
  },
  // SPEAKING SET 6
  {
    topic: 'Sports and Leisure',
    introduction: "Let's speak about sports and leisure activities.",
    questions: [
      "How often do you exercise?",
      "What sports are popular in your country?",
      "Did you play sports at school?",
      "Do you prefer watching or playing sports?",
      "What new sport would you like to try?",
    ],
  },
  // SPEAKING SET 7
  {
    topic: 'Food and Healthy Living',
    introduction: "Let's speak about food and healthy living.",
    questions: [
      "What is your favourite food?",
      "Do you usually eat healthy food?",
      "Do you enjoy cooking? What can you cook?",
      "What is a traditional meal in your country?",
      "Do you think people are becoming more health-conscious?",
    ],
  },
  // SPEAKING SET 8
  {
    topic: 'Computers',
    introduction: "Let's speak about computers.",
    questions: [
      "How often do you use a computer?",
      "When did you first start using one?",
      "What do you usually do on a computer?",
      "Are computers important in your daily life?",
      "Do you prefer a computer or a phone for browsing?",
    ],
  },
  // SPEAKING SET 9
  {
    topic: 'Social Media',
    introduction: "Let's speak about social media.",
    questions: [
      "Do you use social media often?",
      "What is your favourite platform?",
      "How has social media changed communication?",
      "Have you ever tried to reduce your time online?",
      "Do you think social media has more positive or negative effects?",
    ],
  },
  // SPEAKING SET 10
  {
    topic: 'Childhood',
    introduction: "Let's speak about your childhood.",
    questions: [
      "Where did you live as a child?",
      "What did you do during summer holidays?",
      "What was your favourite toy?",
      "Do you still keep in touch with childhood friends?",
      "How was school different when you were young?",
    ],
  },
  // SPEAKING SET 11
  {
    topic: 'Technology at Work',
    introduction: "Let's speak about technology at work or in studies.",
    questions: [
      "What technology do you use at work or university?",
      "Has technology changed the way people work in your country?",
      "Are there disadvantages to technology in the workplace?",
      "Would you like to learn more technical skills?",
      "Do you think technology will replace some jobs?",
    ],
  },
  // SPEAKING SET 12
  {
    topic: 'Music',
    introduction: "Let's speak about music.",
    questions: [
      "Do you like listening to music?",
      "What kind of music do you prefer?",
      "When do you usually listen to music?",
      "Have you ever learned to play an instrument?",
      "Why is music important in people's lives?",
    ],
  },
];

export const SPEAKING_TOPICS_PART3: SpeakingTopic[] = [
  // SET 1
  {
    topic: 'Shopping and Fashion',
    introduction: "Let's move on to talk about shopping and fashion.",
    questions: [
      "Do you enjoy shopping?",
      "Do you prefer shopping online or in physical stores?",
      "How important are clothes in expressing personality?",
      "Do you think people spend too much money on fashion?",
      "Has your style changed compared to when you were younger?",
    ],
  },
  // SET 2
  {
    topic: 'Culture',
    introduction: "Let's move on to talk about culture.",
    questions: [
      "How would you describe your culture?",
      "What traditional festivals are important in your country?",
      "What traditional foods are popular where you live?",
      "Is it important to preserve cultural traditions?",
      "Have you ever experienced culture shock?",
    ],
  },
  // SET 3
  {
    topic: 'Apps and the Internet',
    introduction: "Let's move on to talk about apps and the Internet.",
    questions: [
      "What apps do you use most frequently?",
      "How has the Internet changed the way people communicate?",
      "Do you spend too much time online?",
      "Are apps useful for students?",
      "Do you think the Internet has more advantages or disadvantages?",
    ],
  },
  // SET 4
  {
    topic: 'TV Programmes',
    introduction: "Let's move on to talk about TV programmes.",
    questions: [
      "Do you often watch TV programmes?",
      "What types of programmes do you like?",
      "Do you usually watch TV alone or with others?",
      "Has television changed compared to the past?",
      "Do you think TV influences people's opinions?",
    ],
  },
  // SET 5
  {
    topic: 'Computers',
    introduction: "Let's move on to talk about computers.",
    questions: [
      "When did you first start using a computer?",
      "What do you usually use computers for?",
      "Are computers important for work or study?",
      "Do you prefer using a computer or a phone for browsing?",
      "Do you think children should learn computer skills at school?",
    ],
  },
  // SET 6
  {
    topic: 'Newspapers',
    introduction: "Let's move on to talk about newspapers.",
    questions: [
      "Do you still read newspapers?",
      "Do you prefer print or online newspapers?",
      "Are newspapers still relevant today?",
      "How have newspapers changed over time?",
      "Do young people read newspapers?",
    ],
  },
  // SET 7
  {
    topic: 'Gifts',
    introduction: "Let's move on to talk about gifts.",
    questions: [
      "What kind of gifts are popular in your country?",
      "How do you usually choose a gift for someone?",
      "Are expensive gifts better than meaningful ones?",
      "Do people feel pressure to give gifts?",
      "Has gift-giving changed in recent years?",
    ],
  },
  // SET 8
  {
    topic: 'Beverages',
    introduction: "Let's move on to talk about beverages.",
    questions: [
      "What is your favourite drink?",
      "Do you prefer tea or coffee?",
      "Do you like hot or cold drinks more?",
      "Is there a traditional drink in your country?",
      "Have your tastes changed over time?",
    ],
  },
  // SPEAKING SET 1
  {
    topic: 'Dreams',
    introduction: "Let's move on to talk about dreams.",
    questions: [
      "Do you often remember your dreams?",
      "What kind of dreams do you usually have?",
      "Have you ever had a dream that strongly influenced you?",
      "Do you think dreams have special meanings?",
      "Would you like to control your dreams? Why?",
    ],
  },
  // SPEAKING SET 2
  {
    topic: 'Goals',
    introduction: "Let's move on to talk about goals.",
    questions: [
      "Do you have any goals for this year?",
      "Why is it important for people to set goals?",
      "How do you track your progress?",
      "Did you have goals as a child?",
      "What makes some people give up on their goals?",
    ],
  },
  // SPEAKING SET 3
  {
    topic: 'Studying Online',
    introduction: "Let's move on to talk about studying online.",
    questions: [
      "Do you enjoy online learning?",
      "What are its advantages?",
      "What challenges do people face when studying online?",
      "Have you taken an online course?",
      "Do you prefer online or classroom learning?",
    ],
  },
  // SPEAKING SET 4
  {
    topic: 'Technology in Daily Life',
    introduction: "Let's move on to talk about technology in daily life.",
    questions: [
      "How often do you use technology?",
      "What devices are essential for you?",
      "Has technology improved communication?",
      "Are there disadvantages to modern technology?",
      "Could people live without it today?",
    ],
  },
  // SPEAKING SET 5
  {
    topic: 'Apps',
    introduction: "Let's move on to talk about apps.",
    questions: [
      "What apps do you use most often?",
      "Have apps changed communication?",
      "Do you think students rely too much on apps?",
      "Have you ever paid for an app?",
      "What makes an app successful?",
    ],
  },
  // SPEAKING SET 6
  {
    topic: 'Newspapers',
    introduction: "Let's move on to talk about newspapers.",
    questions: [
      "Do you read newspapers?",
      "Do you prefer print or online news?",
      "What kind of news interests you?",
      "Are newspapers still important?",
      "Do young people read newspapers?",
    ],
  },
  // SPEAKING SET 7
  {
    topic: 'Movies',
    introduction: "Let's move on to talk about movies.",
    questions: [
      "Do you enjoy watching movies?",
      "What types of films do you like?",
      "Do you prefer watching movies at home or at the cinema?",
      "Have your tastes in films changed over time?",
      "Why do you think films are so popular worldwide?",
    ],
  },
  // SPEAKING SET 8
  {
    topic: 'Shopping and Fashion',
    introduction: "Let's move on to talk about shopping and fashion.",
    questions: [
      "Do you enjoy shopping?",
      "Do you prefer shopping centres or traditional markets?",
      "What types of clothes do you usually wear?",
      "Are you a fashionable person?",
      "Has online shopping changed people's habits?",
    ],
  },
  // SPEAKING SET 9
  {
    topic: 'Beverages',
    introduction: "Let's move on to talk about beverages.",
    questions: [
      "What is your favourite drink?",
      "Do you prefer tea or coffee?",
      "Is there a traditional drink in your country?",
      "Have your preferences changed over time?",
      "Do you think sugary drinks should be limited?",
    ],
  },
  // SPEAKING SET 10
  {
    topic: 'Emails',
    introduction: "Let's move on to talk about emails.",
    questions: [
      "Do you often send emails?",
      "What kind of emails do you usually write?",
      "Do you prefer email or phone communication?",
      "How often do you check your email?",
      "Have you ever received an email that made you happy?",
    ],
  },
  // SPEAKING SET 11
  {
    topic: 'Reading',
    introduction: "Let's move on to talk about reading.",
    questions: [
      "Do you enjoy reading books?",
      "What kind of books do you prefer?",
      "Did you read much as a child?",
      "Do you prefer printed books or e-books?",
      "Are reading habits changing nowadays?",
    ],
  },
  // SPEAKING SET 12
  {
    topic: 'News',
    introduction: "Let's move on to talk about news.",
    questions: [
      "How often do you follow the news?",
      "Where do you usually get your news from?",
      "What kind of news interests you?",
      "Is it important to stay informed?",
      "Do you think young people care about the news?",
    ],
  },
];

// Legacy format for backward compatibility
export const SPEAKING_QUESTIONS: Record<1 | 2 | 3, string[]> = {
  1: SPEAKING_TOPICS_PART1.flatMap(t => [t.introduction, ...t.questions]),
  2: SPEAKING_TOPICS_PART2.flatMap(t => [t.introduction + '\n\n' + t.questions[0]]),
  3: SPEAKING_TOPICS_PART3.flatMap(t => [t.introduction, ...t.questions]),
};

// Sample Writing Prompts
export const WRITING_PROMPTS: { task1: IELTSWritingTask[]; task2: IELTSWritingTask[] } = {
  task1: [
    {
      task: 1,
      title: 'Complaint Letter',
      description: 'Write a formal letter of complaint.',
      prompt: 'You recently bought a piece of equipment for your kitchen but it did not work. You phoned the shop but no action was taken.\n\nWrite a letter to the shop manager. In your letter:\n- describe the problem with the equipment\n- explain what happened when you phoned the shop\n- say what you would like the manager to do\n\nYou do NOT need to write any addresses. Begin your letter as follows: Dear Sir or Madam,',
      wordCount: { min: 150, max: 200 },
      timeMinutes: 30,
      type: 'general',
    },
    {
      task: 1,
      title: 'Request Letter',
      description: 'Write a letter requesting information or help.',
      prompt: 'You are going to spend your vacation in a city abroad. A friend of yours lives there.\n\nWrite a letter to your friend. In your letter:\n- explain why you are going to this city\n- describe what you would like to do and see there\n- ask for some advice about places to stay\n\nYou do NOT need to write any addresses. Begin your letter as follows: Dear [friend\'s name],',
      wordCount: { min: 150, max: 200 },
      timeMinutes: 30,
      type: 'general',
    },
  ],
  task2: [
    {
      task: 2,
      title: 'Opinion Essay',
      description: 'Present your opinion with supporting arguments.',
      prompt: 'Some people believe that technology has made our lives more complicated, while others think it has made our lives easier.\n\nDiscuss both views and give your own opinion.',
      wordCount: { min: 250, max: 300 },
      timeMinutes: 45,
      type: 'academic',
    },
    {
      task: 2,
      title: 'Problem/Solution Essay',
      description: 'Discuss the problems and propose solutions.',
      prompt: 'Many cities around the world are facing problems related to traffic congestion and air pollution.\n\nWhat are the causes of these problems? What solutions would you suggest?',
      wordCount: { min: 250, max: 300 },
      timeMinutes: 45,
      type: 'academic',
    },
  ],
};

// Sample Reading Passages
export const READING_PASSAGES: IELTSReadingPassage[] = [
  {
    id: 'passage-1',
    title: 'The History of Coffee',
    text: `Coffee is one of the world's most popular beverages, with billions of cups consumed every day. The history of coffee dates back centuries, with its origins traced to the ancient coffee forests of Ethiopia. According to legend, a goat herder named Kaldi first discovered the potential of these beloved beans when he noticed that his goats became unusually energetic after eating berries from a certain tree.

The knowledge of coffee spread from Ethiopia to the Arabian Peninsula, where coffee cultivation and trade began. By the 15th century, coffee was being grown in the Yemeni district of Arabia and by the 16th century, it had spread to Persia, Egypt, Syria, and Turkey. Coffee houses, known as "qahveh khaneh," began to appear in cities across the Near East. These establishments became important social hubs where people gathered to drink coffee, listen to music, watch performers, play chess, and discuss the news.

European travelers to the Near East brought back stories of the unusual dark black beverage. By the 17th century, coffee had made its way to Europe and was becoming popular across the continent. Some people reacted to this new beverage with suspicion or fear, calling it the "bitter invention of Satan." The local clergy condemned coffee when it came to Venice in 1615. The controversy was so great that Pope Clement VIII was asked to intervene. He decided to taste the beverage for himself before making a decision, and found it so satisfying that he gave it papal approval.

Despite such controversy, coffee houses were quickly becoming centers of social activity and communication in the major cities of England, Austria, France, Germany, and Holland. In England, "penny universities" sprang up, so called because for the price of a penny one could purchase a cup of coffee and engage in stimulating conversation.

Coffee came to the Americas by means of Captain Gabriel de Clieu, a young naval officer. In 1723, he obtained a seedling from the King's coffee plant and transported it to Martinique. This single seedling was the parent of all coffee trees throughout the Caribbean, South, and Central America. Today, Brazil is the world's largest coffee producer, followed by Vietnam and Colombia.

The coffee industry employs millions of people worldwide and is worth over $100 billion annually. Coffee has become an integral part of modern culture, with coffee shops serving as meeting places and workspaces. The rise of specialty coffee in recent decades has led to increased interest in coffee origins, processing methods, and brewing techniques.`,
    questions: [
      {
        id: 'q1',
        type: 'multiple_choice',
        question: 'According to the passage, who first discovered coffee?',
        options: ['A farmer in Yemen', 'A goat herder named Kaldi', 'Pope Clement VIII', 'Captain Gabriel de Clieu'],
        correctAnswer: 'A goat herder named Kaldi',
      },
      {
        id: 'q2',
        type: 'multiple_choice',
        question: 'What were coffee houses in the Near East called?',
        options: ['Penny universities', 'Qahveh khaneh', 'Coffee forests', 'Social hubs'],
        correctAnswer: 'Qahveh khaneh',
      },
      {
        id: 'q3',
        type: 'true_false_not_given',
        question: 'Coffee was immediately accepted by everyone in Europe when it first arrived.',
        options: ['True', 'False', 'Not Given'],
        correctAnswer: 'False',
      },
      {
        id: 'q4',
        type: 'true_false_not_given',
        question: 'Pope Clement VIII banned coffee after tasting it.',
        options: ['True', 'False', 'Not Given'],
        correctAnswer: 'False',
      },
      {
        id: 'q5',
        type: 'true_false_not_given',
        question: 'Captain de Clieu brought multiple coffee plants to the Americas.',
        options: ['True', 'False', 'Not Given'],
        correctAnswer: 'False',
      },
      {
        id: 'q6',
        type: 'fill_in_blanks',
        question: 'By the 15th century, coffee was being grown in the _____ district of Arabia.',
        correctAnswer: 'Yemeni',
      },
      {
        id: 'q7',
        type: 'fill_in_blanks',
        question: 'In England, coffee houses were nicknamed "_____" because a cup of coffee cost one penny.',
        correctAnswer: 'penny universities',
      },
      {
        id: 'q8',
        type: 'multiple_choice',
        question: 'Which country is currently the world\'s largest coffee producer?',
        options: ['Ethiopia', 'Colombia', 'Brazil', 'Vietnam'],
        correctAnswer: 'Brazil',
      },
      {
        id: 'q9',
        type: 'short_answer',
        question: 'In what year did Captain Gabriel de Clieu transport the coffee seedling to Martinique?',
        correctAnswer: '1723',
      },
      {
        id: 'q10',
        type: 'multiple_choice',
        question: 'What is the main purpose of this passage?',
        options: [
          'To explain how to grow coffee',
          'To describe the history and spread of coffee',
          'To compare different types of coffee',
          'To argue that coffee is harmful'
        ],
        correctAnswer: 'To describe the history and spread of coffee',
      },
      {
        id: 'q11',
        type: 'true_false_not_given',
        question: 'The coffee industry is worth more than $100 billion annually.',
        options: ['True', 'False', 'Not Given'],
        correctAnswer: 'True',
      },
      {
        id: 'q12',
        type: 'fill_in_blanks',
        question: 'Coffee first arrived in Venice in the year _____.',
        correctAnswer: '1615',
      },
      {
        id: 'q13',
        type: 'multiple_choice',
        question: 'What happened at coffee houses in the Near East?',
        options: [
          'Only coffee was served',
          'People gathered for social activities',
          'Coffee was banned',
          'Only wealthy people were allowed'
        ],
        correctAnswer: 'People gathered for social activities',
      },
    ],
    timeMinutes: 20,
  },
  {
    id: 'passage-2',
    title: 'The Science of Sleep',
    text: `Sleep is a fundamental biological process that affects every aspect of our health and well-being. Despite spending roughly one-third of our lives asleep, scientists are still uncovering the mysteries of why we sleep and what happens in our brains during this essential activity.

During sleep, the brain cycles through different stages, broadly categorized into two types: rapid eye movement (REM) sleep and non-REM sleep. Non-REM sleep consists of three stages, each progressively deeper. Stage 1 is a light sleep from which you can be easily awakened. Stage 2 is a slightly deeper sleep where your heart rate slows and body temperature drops. Stage 3 is deep sleep, also known as slow-wave sleep, which is crucial for physical restoration and immune function.

REM sleep, which typically occurs about 90 minutes after falling asleep, is when most dreaming occurs. During REM sleep, the brain is highly active, almost as active as when awake, but the body's voluntary muscles are temporarily paralyzed. This paralysis, known as atonia, prevents us from acting out our dreams. REM sleep is thought to play a vital role in learning, memory consolidation, and emotional processing.

The amount of sleep needed varies by age. Newborns require 14-17 hours of sleep per day, while teenagers need 8-10 hours. Adults generally need 7-9 hours, though some people naturally require more or less. Sleep needs can also vary based on individual factors such as genetics, health conditions, and lifestyle.

Sleep deprivation has serious consequences for health and cognitive function. Short-term effects include impaired attention, reduced reaction time, and mood changes. Chronic sleep deprivation has been linked to numerous health problems, including obesity, diabetes, cardiovascular disease, and weakened immune function. Studies have shown that people who consistently get less than six hours of sleep per night have a higher risk of early mortality.

The circadian rhythm, our internal biological clock, plays a crucial role in regulating sleep patterns. This 24-hour cycle is controlled by a small region of the brain called the suprachiasmatic nucleus (SCN), which responds to light signals from the eyes. Exposure to artificial light, particularly blue light from electronic devices, can disrupt this natural rhythm and make it harder to fall asleep.

Modern society presents numerous challenges to healthy sleep. Shift work, jet lag, and the constant connectivity enabled by technology all contribute to widespread sleep problems. Approximately 50-70 million Americans suffer from sleep disorders, with insomnia being the most common. Other disorders include sleep apnea, narcolepsy, and restless leg syndrome.

Improving sleep hygiene can help address many sleep problems. Recommendations include maintaining a consistent sleep schedule, creating a dark and quiet sleep environment, avoiding caffeine and alcohol before bed, and limiting screen time in the evening. Regular exercise has also been shown to improve sleep quality, though vigorous exercise should be avoided close to bedtime.`,
    questions: [
      {
        id: 'q14',
        type: 'multiple_choice',
        question: 'How much of our lives do we typically spend sleeping?',
        options: ['One-quarter', 'One-third', 'One-half', 'Two-thirds'],
        correctAnswer: 'One-third',
      },
      {
        id: 'q15',
        type: 'multiple_choice',
        question: 'Which stage of sleep is crucial for physical restoration?',
        options: ['Stage 1', 'Stage 2', 'Stage 3 (slow-wave sleep)', 'REM sleep'],
        correctAnswer: 'Stage 3 (slow-wave sleep)',
      },
      {
        id: 'q16',
        type: 'true_false_not_given',
        question: 'The brain is completely inactive during REM sleep.',
        options: ['True', 'False', 'Not Given'],
        correctAnswer: 'False',
      },
      {
        id: 'q17',
        type: 'true_false_not_given',
        question: 'Atonia during REM sleep prevents people from physically acting out their dreams.',
        options: ['True', 'False', 'Not Given'],
        correctAnswer: 'True',
      },
      {
        id: 'q18',
        type: 'fill_in_blanks',
        question: 'REM sleep typically occurs about _____ minutes after falling asleep.',
        correctAnswer: '90',
      },
      {
        id: 'q19',
        type: 'multiple_choice',
        question: 'How many hours of sleep do adults generally need?',
        options: ['5-6 hours', '7-9 hours', '10-12 hours', '14-17 hours'],
        correctAnswer: '7-9 hours',
      },
      {
        id: 'q20',
        type: 'short_answer',
        question: 'What is the name of the brain region that controls the circadian rhythm?',
        correctAnswer: 'suprachiasmatic nucleus',
      },
      {
        id: 'q21',
        type: 'true_false_not_given',
        question: 'Blue light from electronic devices helps people fall asleep faster.',
        options: ['True', 'False', 'Not Given'],
        correctAnswer: 'False',
      },
      {
        id: 'q22',
        type: 'multiple_choice',
        question: 'What is the most common sleep disorder mentioned in the passage?',
        options: ['Sleep apnea', 'Narcolepsy', 'Insomnia', 'Restless leg syndrome'],
        correctAnswer: 'Insomnia',
      },
      {
        id: 'q23',
        type: 'fill_in_blanks',
        question: 'Approximately _____-_____ million Americans suffer from sleep disorders.',
        correctAnswer: '50-70',
      },
      {
        id: 'q24',
        type: 'true_false_not_given',
        question: 'Vigorous exercise right before bedtime improves sleep quality.',
        options: ['True', 'False', 'Not Given'],
        correctAnswer: 'False',
      },
      {
        id: 'q25',
        type: 'multiple_choice',
        question: 'According to the passage, what increases the risk of early mortality?',
        options: [
          'Sleeping more than 9 hours',
          'Consistently getting less than 6 hours of sleep',
          'REM sleep deprivation only',
          'Sleeping at irregular times'
        ],
        correctAnswer: 'Consistently getting less than 6 hours of sleep',
      },
      {
        id: 'q26',
        type: 'short_answer',
        question: 'What term describes the temporary muscle paralysis during REM sleep?',
        correctAnswer: 'atonia',
      },
    ],
    timeMinutes: 20,
  },
  {
    id: 'passage-3',
    title: 'Climate Change and Ocean Ecosystems',
    text: `The world's oceans are experiencing unprecedented changes due to climate change, with far-reaching consequences for marine ecosystems and the billions of people who depend on them. Rising temperatures, ocean acidification, and changing circulation patterns are transforming life beneath the waves in ways that scientists are only beginning to understand.

Ocean temperatures have increased significantly over the past century, with the rate of warming accelerating in recent decades. The ocean absorbs approximately 90% of the excess heat trapped by greenhouse gases, acting as a giant heat sink for the planet. This warming affects marine life in numerous ways, from shifting species distributions to disrupting reproduction cycles.

One of the most visible impacts of ocean warming is coral bleaching. Coral reefs, often called the "rainforests of the sea," support approximately 25% of all marine species despite covering less than 1% of the ocean floor. When water temperatures rise above normal levels, corals expel the symbiotic algae living in their tissues, causing them to turn white and eventually die if conditions don't improve. Mass bleaching events have become increasingly frequent, with the Great Barrier Reef experiencing severe bleaching in 2016, 2017, 2020, and 2022.

Ocean acidification, sometimes called "the other CO2 problem," poses another significant threat. The ocean absorbs about 30% of the carbon dioxide released into the atmosphere. When CO2 dissolves in seawater, it forms carbonic acid, lowering the ocean's pH. Since the beginning of the industrial era, ocean acidity has increased by approximately 30%. This acidification makes it difficult for shellfish, corals, and some plankton species to build their calcium carbonate shells and skeletons.

Marine species are responding to changing conditions by moving to new locations. Many species are shifting toward the poles at rates of up to 70 kilometers per decade, seeking cooler waters. This redistribution is creating "novel ecosystems" where species that have never interacted before are now competing for resources. Tropical species are appearing in temperate waters, while some cold-water species are running out of suitable habitat.

The impacts extend to commercial fisheries, which provide food security and livelihoods for hundreds of millions of people worldwide. Fish stocks are shifting, with some regions experiencing declines while others see increases. Traditional fishing grounds may no longer support the same species, forcing communities to adapt their practices or face economic hardship.

Oxygen levels in the ocean are also declining, a phenomenon known as ocean deoxygenation. Warmer water holds less dissolved oxygen, and changing circulation patterns reduce the mixing of oxygen-rich surface waters with deeper layers. Low-oxygen "dead zones" are expanding in coastal areas and open ocean regions, threatening marine life that cannot escape to better-oxygenated waters.

Sea level rise, driven by thermal expansion of warming water and melting ice sheets, threatens coastal ecosystems such as mangroves, salt marshes, and estuaries. These ecosystems serve as nurseries for many fish species and provide natural barriers against storms and flooding. Their loss would have cascading effects throughout marine food webs.

Addressing these challenges requires both reducing greenhouse gas emissions and implementing adaptation strategies. Marine protected areas can help build ecosystem resilience, while sustainable fishing practices can maintain food supplies. However, many scientists argue that without significant reductions in carbon emissions, the changes already underway will accelerate, fundamentally altering ocean ecosystems for generations to come.`,
    questions: [
      {
        id: 'q27',
        type: 'multiple_choice',
        question: 'What percentage of excess heat from greenhouse gases does the ocean absorb?',
        options: ['About 30%', 'About 50%', 'About 70%', 'About 90%'],
        correctAnswer: 'About 90%',
      },
      {
        id: 'q28',
        type: 'fill_in_blanks',
        question: 'Coral reefs support approximately _____% of all marine species.',
        correctAnswer: '25',
      },
      {
        id: 'q29',
        type: 'true_false_not_given',
        question: 'Coral reefs cover more than 5% of the ocean floor.',
        options: ['True', 'False', 'Not Given'],
        correctAnswer: 'False',
      },
      {
        id: 'q30',
        type: 'multiple_choice',
        question: 'What causes coral bleaching?',
        options: [
          'Ocean acidification only',
          'Corals expelling symbiotic algae due to warm water',
          'Lack of sunlight',
          'Overfishing'
        ],
        correctAnswer: 'Corals expelling symbiotic algae due to warm water',
      },
      {
        id: 'q31',
        type: 'fill_in_blanks',
        question: 'Since the industrial era, ocean acidity has increased by approximately _____.',
        correctAnswer: '30%',
      },
      {
        id: 'q32',
        type: 'true_false_not_given',
        question: 'All marine species are moving toward the equator to find warmer waters.',
        options: ['True', 'False', 'Not Given'],
        correctAnswer: 'False',
      },
      {
        id: 'q33',
        type: 'multiple_choice',
        question: 'At what rate are some marine species moving toward the poles?',
        options: ['10 km per decade', '30 km per decade', '70 km per decade', '100 km per decade'],
        correctAnswer: '70 km per decade',
      },
      {
        id: 'q34',
        type: 'short_answer',
        question: 'What is the term used to describe the declining oxygen levels in the ocean?',
        correctAnswer: 'ocean deoxygenation',
      },
      {
        id: 'q35',
        type: 'true_false_not_given',
        question: 'Warmer water holds more dissolved oxygen than cooler water.',
        options: ['True', 'False', 'Not Given'],
        correctAnswer: 'False',
      },
      {
        id: 'q36',
        type: 'multiple_choice',
        question: 'Which coastal ecosystems are threatened by sea level rise?',
        options: [
          'Only coral reefs',
          'Mangroves, salt marshes, and estuaries',
          'Only sandy beaches',
          'Deep ocean trenches'
        ],
        correctAnswer: 'Mangroves, salt marshes, and estuaries',
      },
      {
        id: 'q37',
        type: 'fill_in_blanks',
        question: 'The ocean absorbs about _____% of the carbon dioxide released into the atmosphere.',
        correctAnswer: '30',
      },
      {
        id: 'q38',
        type: 'true_false_not_given',
        question: 'Marine protected areas can help build ecosystem resilience.',
        options: ['True', 'False', 'Not Given'],
        correctAnswer: 'True',
      },
      {
        id: 'q39',
        type: 'multiple_choice',
        question: 'What is ocean acidification sometimes called?',
        options: [
          'The warming problem',
          'The other CO2 problem',
          'The marine crisis',
          'The calcium problem'
        ],
        correctAnswer: 'The other CO2 problem',
      },
      {
        id: 'q40',
        type: 'short_answer',
        question: 'In which years did the Great Barrier Reef experience severe bleaching events (list any two)?',
        correctAnswer: ['2016', '2017', '2020', '2022'],
      },
    ],
    timeMinutes: 20,
  },
];
